import { useMemo } from 'react';
import { SettingsSection } from '@frontend/settings-ui';
import { ContentLoader, useDebouncedValue, useForm } from '@frontend/design-system';
import type { AvailabilityMode } from '../../../../../components/availability/availability-form/types';
import { AddEditAvailabilityRuleTray } from '../../../../../components/availability/availability-rules/add-edit-availability-rule-tray';
import { useAvailabilityRuleTray } from '../../../../../hooks/settings/use-availability-rule-tray';
import { useGetSourceDropdownFilterDetails } from '../../../../../hooks/use-get-source-dropdown-filter-details';
import { AvailabilityRuleEntity, useGetAvailabilityRules } from '../../hooks/use-get-availability-rules';
import { AvailabilityRuleEmptyState } from './empty-state';
import { Header } from './header';
import { matchesAvailabilityRuleSearch } from './helpers';
import { AvailabilityRuleListContainer } from './list-container';

type Props = {
  availabilityMode: AvailabilityMode;
  locationId: string;
};

export const AvailabilityRuleListSection = ({ availabilityMode, locationId }: Props) => {
  const { selectedSourceIds, setSelectedSourceIds, sourceOptions, hasMultipleSources } =
    useGetSourceDropdownFilterDetails();

  const { getFieldProps, values } = useForm({
    fields: {
      search: { type: 'text', value: '' },
    },
  });

  const {
    editModalOnClose,
    editModalProps,
    isEditMode,
    selectedScheduleDefinition,
    openAddEditTray,
    selectedResourceId,
  } = useAvailabilityRuleTray();

  const debouncedSearchValue = useDebouncedValue(values.search || '');
  const hasSearchValue = !!debouncedSearchValue;

  const { data, isLoading, isFetching, refetch } = useGetAvailabilityRules({
    locationId,
    sourceIds: selectedSourceIds,
    searchValue: '',
    availabilityMode,
  });

  // TODO: Remove client side searching when API starts supports search by rule name, provider, operatory and service
  const filteredData = useMemo(
    () => data.filter((availabilityRule) => matchesAvailabilityRuleSearch(availabilityRule, debouncedSearchValue)),
    [data, debouncedSearchValue]
  );

  const showSearchbox = (!isLoading && data.length > 0) || hasSearchValue;

  return (
    <SettingsSection>
      <ContentLoader show={isLoading} />
      <Header
        showSourceDropdown={hasMultipleSources}
        showSearchbox={showSearchbox}
        sourceOptions={sourceOptions}
        onSelectedSourcesChange={(sources) => setSelectedSourceIds(sources)}
        getFieldProps={getFieldProps}
        isFetching={isFetching}
        refetch={refetch}
      />
      {filteredData?.map((availabilityRuleEntity: AvailabilityRuleEntity) => (
        <AvailabilityRuleListContainer
          key={availabilityRuleEntity.id}
          availabilityMode={availabilityMode}
          availabilityRuleEntity={availabilityRuleEntity}
          locationId={locationId}
          openAddEditTray={openAddEditTray}
          sourceOptions={sourceOptions}
          sourceIds={selectedSourceIds}
        />
      ))}
      {!isLoading && !filteredData.length && (
        <AvailabilityRuleEmptyState availabilityMode={availabilityMode} hasSearchValue={hasSearchValue} />
      )}
      {editModalProps.show && (
        <AddEditAvailabilityRuleTray
          modalProps={editModalProps}
          locationId={locationId}
          onClose={editModalOnClose}
          availabilityMode={availabilityMode}
          selectedResourceId={selectedResourceId}
          selectedScheduleDefinition={selectedScheduleDefinition}
          isEditMode={isEditMode}
        />
      )}
    </SettingsSection>
  );
};
